// ABOUTME: Modal for editing behavior event details (type, notes, time, child assignment)
// ABOUTME: Includes validation for event time constraints and child selection

import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Typography
} from '@mui/material';
import { StarOutlineRounded } from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import dayjs, { Dayjs } from 'dayjs';
import { BehaviorType } from '../../../types/api';
import { getBehaviorIcon, TriangleIcon, StormIcon, PikoIcon } from '../../../utils/behaviorUtils';
import { formatRelativeTime, formatEventLocalTime, formatUtcTimeInTimezone } from '../../../utils/timeUtils';
import { BehaviorEventEntry } from '../../BehaviorEventsTable';
import type { EditEventFormState } from '../types';

interface EditBehaviorModalProps {
  open: boolean;
  selectedBehavior: BehaviorEventEntry | null;
  editEventData: EditEventFormState;
  dateValidationError: string | null;
  familyChildren: Array<{ id: number; name: string }>;
  familyTimezone?: string;
  onClose: () => void;
  onSave: () => void;
  onFormChange: (updates: Partial<EditEventFormState>) => void;
  onDateChange: (newValue: Dayjs | null) => void;
}

const EditBehaviorModal: React.FC<EditBehaviorModalProps> = ({
  open,
  selectedBehavior,
  editEventData,
  dateValidationError,
  familyChildren,
  familyTimezone,
  onClose,
  onSave,
  onFormChange,
  onDateChange
}) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Edit Event</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1 }}>
          {/* Child Selection Dropdown */}
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Child</InputLabel>
            <Select
              value={editEventData.childId}
              label="Child"
              onChange={(e) => onFormChange({ childId: e.target.value as number })}
            >
              {familyChildren.map((childOption) => (
                <MenuItem key={childOption.id} value={childOption.id}>
                  {childOption.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Behavior Type Selection */}
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Behavior Type</InputLabel>
            <Select
              value={editEventData.behaviorType}
              label="Behavior Type"
              onChange={(e) => onFormChange({ behaviorType: e.target.value as BehaviorType })}
            >
              <MenuItem value={BehaviorType.Star}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <StarOutlineRounded sx={{ mr: 1, color: 'success.main' }} />
                  Positive
                </Box>
              </MenuItem>
              <MenuItem value={BehaviorType.Triangle}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <TriangleIcon sx={{ mr: 1 }} />
                  Warning
                </Box>
              </MenuItem>
              <MenuItem value={BehaviorType.Square}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <StormIcon sx={{ mr: 1 }} />
                  Consequence
                </Box>
              </MenuItem>
              <MenuItem value={BehaviorType.FlipYourCoin}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <PikoIcon sx={{ mr: 1 }} />
                  Reset
                </Box>
              </MenuItem>
              <MenuItem value={BehaviorType.MakeItRight}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {getBehaviorIcon(BehaviorType.MakeItRight, { sx: { mr: 1 } })}
                  Make It Right
                </Box>
              </MenuItem>
              <MenuItem value={BehaviorType.OneOnOneTime}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {getBehaviorIcon(BehaviorType.OneOnOneTime, { sx: { mr: 1 } })}
                  Let Go
                </Box>
              </MenuItem>
            </Select>
          </FormControl>

          <TextField
            fullWidth
            multiline
            rows={3}
            label="Notes"
            value={editEventData.notes}
            onChange={(e) => onFormChange({ notes: e.target.value })}
            placeholder="Add notes about this behavior..."
            sx={{ mb: 2 }}
          />

          {/* Current Event Time Display */}
          {selectedBehavior && (() => {
            // Determine which time field to use for display (eventLocalTime > eventTime > recordedAt)
            const displayTime = selectedBehavior.eventLocalTime || selectedBehavior.eventTime || selectedBehavior.recordedAt;

            // Event was edited if EventTime exists and differs from CreatedAt (recordedAt)
            // Both are UTC timestamps, so we can compare them directly
            const wasEdited = selectedBehavior.eventTime &&
              selectedBehavior.eventTime !== selectedBehavior.recordedAt &&
              dayjs(selectedBehavior.eventTime).format('YYYY-MM-DD HH:mm') !== dayjs(selectedBehavior.recordedAt).format('YYYY-MM-DD HH:mm');

            return (
              <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  {wasEdited ? 'Current Event Time' : 'Event Time'}
                </Typography>
                <Typography variant="h6" color="text.primary" gutterBottom>
                  {formatEventLocalTime(displayTime)}
                </Typography>
                {wasEdited && (
                  <Typography variant="caption" color="text.secondary">
                    Originally logged: {formatUtcTimeInTimezone(selectedBehavior.recordedAt, familyTimezone)} ({formatRelativeTime(selectedBehavior.recordedAt, familyTimezone)})
                  </Typography>
                )}
              </Box>
            );
          })()}

          {/* Date/Time Controls */}
          <Box sx={{ mb: 2 }}>
            <DateTimePicker
              label="New event time (optional)"
              value={editEventData.newEventTime}
              onChange={(newValue) => onDateChange(newValue as Dayjs | null)}
              slotProps={{
                textField: {
                  fullWidth: true,
                  helperText: 'Leave blank to keep current time',
                  error: Boolean(dateValidationError),
                }
              }}
            />
            {dateValidationError && (
              <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                {dateValidationError}
              </Typography>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={onSave} variant="contained">Save</Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditBehaviorModal;
